<template>
  <button
    :id="id"
    :data-testid="testId"
    :class="[
      'transition-colors duration-200',
      'focus:outline-none focus:ring-0 focus:ring-offset-0',
      sizeClasses,
      shapeClasses,
      variantClasses,
      disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
      variant === 'white' && 'bg-white border border-gray-300 text-gray-900 hover:bg-gray-50 focus:ring-2 focus:ring-emerald-200'
    ]"
    :disabled="disabled"
    :type="type"
    :aria-label="ariaLabel"
    v-bind="$attrs"
  >
    <slot />
  </button>
</template>

<script setup lang="ts">
import { type ClassNameValue, twMerge } from 'tailwind-merge'

defineOptions({ inheritAttrs: false })

type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'success' | 'white'
type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'
type ButtonShape = 'square' | 'rounded' | 'pill'

const props = withDefaults(defineProps<{
  id?: string
  testId?: string
  variant?: ButtonVariant
  size?: ButtonSize
  shape?: ButtonShape
  disabled?: boolean
  type?: 'button' | 'submit' | 'reset'
  ariaLabel?: string
}>(), {
  variant: 'primary',
  size: 'md',
  shape: 'rounded',
  disabled: false,
  type: 'button'
})

const variantClasses = computed(() => {
  const base = {
    primary: 'bg-blue-600 text-white hover:bg-blue-700',
    secondary: 'bg-gray-600 text-white hover:bg-gray-700',
    outline: 'border border-gray-300 text-gray-700 hover:bg-gray-50',
    ghost: 'text-gray-700 hover:bg-gray-100',
    danger: 'bg-red-600 text-white hover:bg-red-700',
    success: 'bg-emerald-600 text-white hover:bg-emerald-700',
    white: 'bg-white border border-gray-300 text-gray-900 hover:bg-gray-50 focus:ring-2 focus:ring-emerald-200'
  }
  return base[props.variant]
})

const sizeClasses = computed(() => {
  const base = {
    xs: 'text-xs px-2 py-1',
    sm: 'text-sm px-3 py-1.5',
    md: 'text-base px-4 py-2',
    lg: 'text-lg px-6 py-3',
    xl: 'text-xl px-8 py-4'
  }
  return base[props.size]
})

const shapeClasses = computed(() => {
  const base = {
    square: 'rounded-none',
    rounded: 'rounded-md',
    pill: 'rounded-full'
  }
  return base[props.shape]
})
</script>